/**
 * Minimal slice of the session project needed to decide whether a project has
 * results worth showing. `status` 4 = Done (there is no frontend ProjectStatus
 * enum; the codebase compares the literal — see `results.component#resultsVisible`).
 */
export interface ViewableResultsProjectSlice {
  status?: number;
  runSummary?: { done?: number } | null;
}

/**
 * True when at least one finished case exists to display.
 *
 * A multi-manufacturing master's status is DERIVED from its methods, and
 * `deriveMasterStatus` only reports Done once every method is terminal. An HPC
 * round-trip imports methods one at a time, so a master with real, renderable
 * results sits at Busy (`[Done, Ready, Ready]` is neither all-terminal nor
 * running) for as long as any method is missing. Gating on `status === 4`
 * therefore hides finished work — the project opens on the config flow instead
 * of Results until the LAST method lands.
 *
 * Deliberately narrower than `projectHasRunResults` in results-lock.util.ts:
 * that one also counts `runSummary.total >= 1` because the results-protection
 * lock must fire for a master whose methods are still running. Here, `total`
 * without `done` would land the user on an empty Results page.
 */
export function hasViewableResults(
  project: ViewableResultsProjectSlice | null | undefined,
): boolean {
  if (!project) return false;
  if (project.status === 4) return true;
  return (project.runSummary?.done ?? 0) >= 1;
}

/**
 * The status to present to code that renders optimized geometry only for
 * status 4 (Done) — `engine.init()` and the flow-step validation skip.
 *
 * A partially imported master's finished methods are already merged and
 * perfectly renderable, so it must be presented as Done. The inner
 * `organicAssemblyAvailable` / `parametricAssemblyAvailable` guards still
 * decide what actually gets drawn.
 */
export function displayStatusFor(
  project: ViewableResultsProjectSlice | null | undefined,
): number {
  return hasViewableResults(project) ? 4 : (project?.status ?? 0);
}
